<script setup>
// Choose a topic — Domain → Subject → Topic drill-down (the agreed taxonomy,
// Phase 2). The category tree comes from GET /api/categories (global backbone +
// the user's tenant extensions); decks come from GET /api/decks and are slotted
// onto their category node by their `category` slug path. Library decks (no path
// yet) and any unresolved decks surface under synthetic "My decks"/"Unsorted"
// nodes. buildPickerModel (lib/categoryTree.js) does the slotting; this view
// owns the drill state + rendering. Picking a deck opens the focused player.
import { ref, computed, onMounted } from "vue";
import DeckCoverCard from "./DeckCoverCard.vue";
import { buildPickerModel } from "../../lib/categoryTree.js";
import { collectFacets, isFilterActive, filterDecks } from "../../lib/deckFacets.js";
import { useAuthStore } from "@digitalstreams/saas-core/vue/stores/authStore";

const auth = useAuthStore();

const loading = ref(true);
const error = ref("");
const roots = ref([]);
const stack = ref([]); // nodes drilled into, root-first
const allDecks = ref([]); // raw /api/decks list, for facet filtering

// Facet filter state (orthogonal to the hierarchy). When active, the picker
// shows a flat filtered result list instead of the drill-down.
const filterTags = ref([]);
const filterDifficulty = ref(null);
const facets = computed(() => collectFacets(allDecks.value));
const filterActive = computed(() => isFilterActive({ tags: filterTags.value, difficulty: filterDifficulty.value }));
const results = computed(() => filterDecks(allDecks.value, { tags: filterTags.value, difficulty: filterDifficulty.value }));

const DOMAIN_COLOR = { healthcare: "#0d9488", "road-safety": "#ea580c" };
const deckColor = (d) => DOMAIN_COLOR[d.category?.[0]] || (d.source === "library" ? "#2563eb" : "#64748b");

function toggleTag(t) {
  filterTags.value = filterTags.value.includes(t)
    ? filterTags.value.filter((x) => x !== t)
    : [...filterTags.value, t];
}
function clearFilters() { filterTags.value = []; filterDifficulty.value = null; }

// Icon per node. Domains get a themed glyph; deeper nodes get folder/topic.
const DOMAIN_ICONS = {
  "healthcare": "bi-heart-pulse",
  "road-safety": "bi-cone-striped",
  "my-decks": "bi-collection",
  "public-decks": "bi-globe2",
  "unsorted": "bi-inbox"
};
function iconFor(node) {
  if (DOMAIN_ICONS[node.slug]) return DOMAIN_ICONS[node.slug];
  if (node.children && node.children.length) return "bi-folder2-open";
  return "bi-journal-text";
}

const current = computed(() => (stack.value.length ? stack.value[stack.value.length - 1] : null));
// Top-level groups (My decks / Public decks / domains) are shown as TABS at the
// root, with the active one's contents rendered straight away — no extra click to
// reach the decks users want. Drilling deeper still uses the stack + breadcrumb.
const activeRootIdx = ref(0);
const activeRoot = computed(() => roots.value[activeRootIdx.value] || null);
const atRoot = computed(() => stack.value.length === 0);
const showTabs = computed(() => atRoot.value && !filterActive.value && roots.value.length > 1);
const cards = computed(() => (stack.value.length ? (current.value?.children || []) : (activeRoot.value?.children || [])));
const decks = computed(() => (stack.value.length ? (current.value?.decks || []) : (activeRoot.value?.decks || [])));

// Breadcrumb: "Topics" root + each drilled node.
const crumbs = computed(() => [
  { name: "Topics", index: -1 },
  ...stack.value.map((n, i) => ({ name: n.name, index: i }))
]);

const isEmpty = computed(() => !loading.value && !error.value && roots.value.length === 0);

function enter(card) {
  if (!((card.children && card.children.length) || (card.decks && card.decks.length))) return;
  // From a root tab, seed the breadcrumb with the active root so context isn't lost.
  stack.value = stack.value.length === 0 ? [activeRoot.value, card] : [...stack.value, card];
}
function goTo(index) {
  stack.value = index < 0 ? [] : stack.value.slice(0, index + 1);
}

async function load() {
  loading.value = true;
  error.value = "";
  try {
    const [cRes, dRes] = await Promise.all([
      fetch("/api/categories", { credentials: "include" }),
      fetch("/api/decks", { credentials: "include" })
    ]);
    if (!dRes.ok) throw new Error(`/api/decks responded ${dRes.status}`);
    const categories = cRes.ok ? (await cRes.json()).categories || [] : [];
    const deckList = (await dRes.json()).decks || [];
    allDecks.value = deckList;
    roots.value = buildPickerModel(categories, deckList).roots;
    // Anonymous / first-time visitors: surface the PUBLIC catalog as the FIRST
    // tab — it's the acquisition hook, so it shouldn't sit behind the built-in
    // domains. (Signed-in users keep the natural order; they default to My decks.)
    if (!auth.isLoggedIn) {
      const pIdx = roots.value.findIndex((r) => r.slug === "public-decks");
      if (pIdx > 0) roots.value = [roots.value[pIdx], ...roots.value.slice(0, pIdx), ...roots.value.slice(pIdx + 1)];
    }
    stack.value = [];
    // Default tab. A signed-in user lands on their own library ("my-decks");
    // an anonymous / first-time visitor lands on the PUBLIC catalog so the
    // public decks are the first thing they see (acquisition UX — don't bury the
    // content behind a tab). Fall back to public, then the first group.
    const prefer = auth.isLoggedIn ? "my-decks" : "public-decks";
    let idx = roots.value.findIndex((r) => r.slug === prefer);
    if (idx < 0) idx = roots.value.findIndex((r) => r.slug === "public-decks");
    if (idx < 0) idx = 0;
    activeRootIdx.value = idx;
  } catch (e) {
    console.error("[topics] picker load failed:", e);
    error.value = "We couldn't load the topics just now.";
  } finally {
    loading.value = false;
  }
}
onMounted(load);

const playHref = (slug) => `/topics/play/${encodeURIComponent(slug)}`;
const subtitle = (node) =>
  `${node.deckCount} ${node.deckCount === 1 ? "deck" : "decks"}`;
</script>

<template>
  <div class="card border-0 shadow-sm">
    <div class="card-body">
      <!-- Anonymous demo nudge — try freely, sign up to author + save progress -->
      <div v-if="!auth.isLoggedIn" class="alert alert-primary d-flex flex-wrap align-items-center gap-2 py-2 mb-3">
        <i class="bi bi-stars"></i>
        <span class="flex-grow-1">You're exploring i-Recall — pick any topic and answer out loud, no sign-up needed.</span>
        <router-link to="/register" class="btn btn-sm btn-primary">Create a free account</router-link>
      </div>

      <div class="d-flex align-items-center justify-content-between mb-3 gap-2 flex-wrap">
        <!-- Breadcrumb -->
        <nav v-if="stack.length && !filterActive" aria-label="breadcrumb" class="mb-0">
          <ol class="breadcrumb mb-0">
            <li
              v-for="c in crumbs"
              :key="c.index"
              class="breadcrumb-item"
              :class="{ active: c.index === stack.length - 1 }"
            >
              <a v-if="c.index !== stack.length - 1" href="#" @click.prevent="goTo(c.index)">{{ c.name }}</a>
              <span v-else>{{ c.name }}</span>
            </li>
          </ol>
        </nav>
        <span v-else class="text-muted small">Browse by topic</span>

        <router-link to="/settings" class="btn btn-sm btn-outline-secondary ms-auto">
          <i class="bi bi-mic me-1"></i>Test microphone
        </router-link>
      </div>

      <!-- Facet filter bar — tags + difficulty (orthogonal to the hierarchy).
           When active, the picker shows a flat filtered result list. -->
      <div
        v-if="!loading && !error && !isEmpty && (facets.tags.length || facets.difficulties.length)"
        class="d-flex flex-wrap align-items-center gap-2 mb-3 pb-2 border-bottom"
      >
        <span class="text-muted small"><i class="bi bi-funnel me-1"></i>Filter</span>
        <button
          v-for="t in facets.tags" :key="t" type="button"
          class="btn btn-sm rounded-pill py-0"
          :class="filterTags.includes(t) ? 'btn-primary' : 'btn-outline-secondary'"
          @click="toggleTag(t)"
        >{{ t }}</button>
        <select
          v-if="facets.difficulties.length"
          v-model.number="filterDifficulty"
          class="form-select form-select-sm w-auto"
        >
          <option :value="null">Any difficulty</option>
          <option v-for="n in facets.difficulties" :key="n" :value="n">Difficulty {{ n }}</option>
        </select>
        <button v-if="filterActive" type="button" class="btn btn-sm btn-link text-decoration-none ms-auto" @click="clearFilters">
          <i class="bi bi-x-lg me-1"></i>Clear
        </button>
      </div>

      <div v-if="loading" class="text-muted py-3">
        <span class="spinner-border spinner-border-sm me-2"></span>Loading topics…
      </div>
      <div v-else-if="error" class="text-center text-muted py-4">
        <i class="bi bi-arrow-clockwise display-6 d-block mb-2"></i>
        <p class="mb-2">{{ error }} <span class="small">This is usually momentary.</span></p>
        <button class="btn btn-outline-secondary btn-sm" @click="load"><i class="bi bi-arrow-clockwise me-1"></i>Try again</button>
      </div>
      <div v-else-if="isEmpty" class="text-center text-muted py-4">
        <i class="bi bi-collection display-6 text-primary opacity-50 d-block mb-2"></i>
        <p class="mb-2">No topics to practise yet.</p>
        <router-link to="/library/new" class="btn btn-primary btn-sm"><i class="bi bi-plus-lg me-1"></i>Create your first deck</router-link>
      </div>

      <template v-else>
        <!-- Filtered (flat) results — across the whole tree -->
        <div v-if="filterActive">
          <p class="text-muted small mb-2">{{ results.length }} {{ results.length === 1 ? "deck" : "decks" }} match</p>
          <div v-if="results.length" class="deck-grid">
            <DeckCoverCard
              v-for="d in results" :key="d.slug"
              :deck="d" :color="deckColor(d)" @play="$router.push(playHref(d.slug))" />
          </div>
          <p v-else class="text-muted py-3 mb-0">
            No decks match these filters. <a href="#" @click.prevent="clearFilters">Clear filters</a>.
          </p>
        </div>

        <!-- Drill-down (no active filter) -->
        <template v-else>
        <!-- Top-level groups as tabs (My decks open by default) — the active
             group's categories + decks render directly below, no extra click. -->
        <ul v-if="showTabs" class="nav nav-tabs mb-3">
          <li v-for="(rt, i) in roots" :key="rt.id || rt.slug" class="nav-item">
            <button
              type="button"
              class="nav-link d-flex align-items-center gap-1"
              :class="{ active: i === activeRootIdx }"
              @click="activeRootIdx = i"
            >
              <i class="bi" :class="iconFor(rt)"></i>
              <span>{{ rt.name }}</span>
              <span class="badge rounded-pill bg-light text-muted border">{{ rt.deckCount }}</span>
            </button>
          </li>
        </ul>

        <!-- Category cards (drill down) -->
        <div v-if="cards.length" class="row g-3 mb-2">
          <div v-for="node in cards" :key="node.id || node.slug" class="col-12 col-md-6 col-lg-4">
            <button
              type="button"
              class="card border h-100 w-100 text-start rm-cat"
              :style="{ borderLeft: '4px solid ' + (node.color || '#64748b') }"
              :disabled="node.deckCount === 0 && !(node.children && node.children.length)"
              @click="enter(node)"
            >
              <div class="card-body d-flex align-items-center">
                <span class="rm-cat-icon" :style="{ background: (node.color || '#64748b') + '22', color: node.color || '#64748b' }">
                  <i class="bi" :class="iconFor(node)"></i>
                </span>
                <div class="ms-2 flex-grow-1 min-w-0">
                  <div class="fw-semibold text-truncate" :title="node.name">{{ node.name }}</div>
                  <div class="text-muted small">{{ subtitle(node) }}</div>
                </div>
                <i class="bi bi-chevron-right text-muted ms-2"></i>
              </div>
            </button>
          </div>
        </div>

        <!-- Decks attached at the current node -->
        <div v-if="decks.length" class="deck-grid" :class="{ 'mt-1': cards.length }">
          <DeckCoverCard
            v-for="d in decks" :key="d.slug"
            :deck="d" :color="(current && current.color) || deckColor(d)" @play="$router.push(playHref(d.slug))" />
        </div>

        <p v-if="!cards.length && !decks.length" class="text-muted mb-0 py-3">
          Nothing here yet.
        </p>
        </template>
      </template>
    </div>
  </div>
</template>

<style scoped>
/* Deck grid — replaces the Bootstrap row/col grid, which stretched a card to a
   third of the viewport on a wide screen (cover art at 600px+ wide reads as a
   banner, not a card).
   Cards cap at 300px (170px on phones) and the grid fills the row with as many
   as fit. `min()` against 50% is what guarantees AT LEAST TWO COLUMNS: however
   narrow the viewport, a track can never demand more than half the row. */
.deck-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fill, minmax(min(300px, calc(50% - 0.5rem)), 1fr));
  justify-items: stretch;
}
.deck-grid > * { max-width: 300px; width: 100%; }

@media (max-width: 575.98px) {
  .deck-grid {
    gap: .625rem;
    grid-template-columns: repeat(auto-fill, minmax(min(170px, calc(50% - 0.3125rem)), 1fr));
  }
  .deck-grid > * { max-width: 170px; }
}

.rm-cat {
  background: #fff;
  transition: box-shadow .12s ease, transform .12s ease;
}
.rm-cat:hover:not(:disabled) { box-shadow: 0 .25rem .75rem rgba(0,0,0,.06); transform: translateY(-1px); }
.rm-cat:disabled { opacity: .55; cursor: default; }
.rm-cat-icon {
  display: inline-flex; align-items: center; justify-content: center;
  width: 34px; height: 34px; border-radius: 9px; font-size: 1rem; flex-shrink: 0;
}
.min-w-0 { min-width: 0; }

/* Tighter cards — ~5 per row on desktop instead of 3 full-width ones. Scoped to
   this picker (Vue scoped style), so other pages' grids are unaffected. */
@media (min-width: 992px) {
  .col-lg-4 { flex: none; width: 20%; max-width: 250px; min-width: 200px; }
}

/* Tone the Practise button down from the in-your-face brand blue to a calm
   slate. The 4px coloured left accent on the card keeps a hint of identity. */
.rm-practise {
  color: #fff;
  background-color: #5e677c !important;
  border-color: #5e677c !important;
}
.rm-practise:hover { color: #fff; filter: brightness(0.93); }
</style>
